import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { submitReview } from '../services/reviewService';

function DeliveredReviewForm({ items }) {
  const { token } = useParams();
  const fileInputRef = useRef(null);
  const [itemId, setItemId] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [image, setImage] = useState(null);
  const [reviewedIds, setReviewedIds] = useState(() =>
    (items || []).filter((item) => item.review).map((item) => item.id)
  );
  const [reviewsByItem, setReviewsByItem] = useState(() =>
    Object.fromEntries(
      (items || [])
        .filter((item) => item.review)
        .map((item) => [item.id, item.review])
    )
  );
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    const existingReview = reviewsByItem[itemId] || items.find((item) => item.id === itemId)?.review;
    try {
      await submitReview({ token, itemId, rating, comment, image });
      setReviewedIds((current) =>
        current.includes(itemId) ? current : [...current, itemId]
      );
      setReviewsByItem((current) => ({
        ...current,
        [itemId]: { ...current[itemId], rating, comment }
      }));
      setMessage(
        existingReview
          ? 'Thank you! Your review has been updated.'
          : 'Thank you! Your review has been published.'
      );
      setItemId('');
      setComment('');
      setImage(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  if (!items?.length) return null;
  const availableItems = items;
  const isEditing = reviewedIds.includes(itemId) || Boolean(reviewsByItem[itemId]);

  return (
    <section className="zevi-delivered-review">
      <h2>Tell us about your saree</h2>
      {availableItems.length > 0 && (
        <form onSubmit={submit}>
          <label>Item
            <select
              required
              value={itemId}
              onChange={(e) => {
                const nextItemId = e.target.value;
                const review = reviewsByItem[nextItemId]
                  || items.find((item) => item.id === nextItemId)?.review;
                setItemId(nextItemId);
                setRating(review?.rating || 5);
                setComment(review?.comment || '');
                setMessage('');
              }}
            >
              <option value="">Select an item</option>
              {availableItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.product_name}
                  {reviewedIds.includes(item.id) || reviewsByItem[item.id] || item.review
                    ? ' — Edit review'
                    : ''}
                </option>
              ))}
            </select>
          </label>
          <label>Rating
            <select value={rating} onChange={(e) => setRating(Number(e.target.value))}>
              {[5, 4, 3, 2, 1].map((value) => <option key={value} value={value}>{value} stars</option>)}
            </select>
          </label>
          <label>Review<textarea maxLength={1500} value={comment}
            onChange={(e) => setComment(e.target.value)} placeholder="How did it feel?" /></label>
          <label>Photo (optional, max 5 MB)
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setImage(e.target.files?.[0] || null)}
            />
          </label>
          {isEditing && (
            <p>Your existing review will be updated. Leave the photo empty to keep its current image.</p>
          )}
          <button type="submit" className="gold-button" disabled={busy || !itemId}>
            {busy ? (isEditing ? 'Saving…' : 'Posting…') : (isEditing ? 'Save review changes' : 'Post review')}
          </button>
        </form>
      )}
      {message && <p role="status">{message}</p>}
    </section>
  );
}

export default DeliveredReviewForm;